import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SnackbarMUI from '../SnackbarMUI';
import Progress from '../Progress';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';

const formatShortDate = (dateString) => {
  const [y, m, d] = String(dateString).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// Tooltip content for PF when the PF rules changed during the month (one line per rule version)
const getPfSplitTooltip = (breakdown) => {
  if (!Array.isArray(breakdown) || breakdown.length < 2) return null;
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>PF split by PF rules in force</div>
      {breakdown.map((part) => (
        <div key={part.from}>
          {formatShortDate(part.from)} – {formatShortDate(part.to)} ({part.days} days):
          {' '}limit ₹{Number(part.pf_limit || 0).toLocaleString('en-IN')} @ {Number(part.pf_percent)}%
          {' '}= ₹{Number(part.pf || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
        </div>
      ))}
    </div>
  );
};

export default function CreatedPayrollTable(props) {
  const navigate = useNavigate();

  const [earningDedComps, setEarningDedComps] = useState([]);

  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  useEffect(() => {
    const fetchEarningDedComps = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/earning-deduction-comps`);
        
        // axios automatically parses the JSON, so data is in response.data
        setEarningDedComps(response.data);
      } catch (error) {
        console.error("Error fetching earning and deduction components:", error);
        // Optionally, you can set a notification here using setNotify
      }
    };

    fetchEarningDedComps();
  }, []); // The empty dependency array ensures this runs only once on mount

  const earningComps = earningDedComps.filter((item)=>item.deductionType === 0);
  const deductionComps = earningDedComps.filter((item)=>item.earningType === 0);

  // --- CHECK NOTICE PERIOD TYPE AND SHOW RELEVANT COLUMNS ---
  const hasRecoveryNotice = (props.payrolls || []).some(p => p.notice_period_type === 'RECOVERY');
  const hasPayoutNotice = (props.payrolls || []).some(p => p.notice_period_type === 'EARNING');
  
  // Recovery: remaining days + recovery amount
  // Payout: notice period pay (shortfall days column hidden from UI)
  const showRemainingNoticeDays = hasRecoveryNotice;
  const showNoticePeriodRecoveryAmount = hasRecoveryNotice;
  const showNoticePeriodShortfallDays = false;
  const showNoticePeriodPay = hasPayoutNotice;

  // --- FUNCTION TO CHECK IF COLUMN HAS ALL ZERO VALUES ---
  const hasAllZeroValues = (columnKey, payrolls) => {
    if (!payrolls || payrolls.length === 0) return false;

    return payrolls.every(payroll => {
      if (payroll.empPayroll.mERROR_MESSAGE !== 'No Error') return true; // Skip error rows

      let value = 0;

      // Check for Total Days
      if (columnKey === 'totalDays') {
        value = Number(payroll.empPayroll.daysInMonth) || 0;
      }
      // Check for Total Worked Days
      else if (columnKey === 'totalWorkedDays') {
        value = Number(payroll.empPayroll.mTOTAL_PRESENT_DAYS) || 0;
      }
      // Check for Total Leaves
      else if (columnKey === 'totalLeaves') {
        value = Number(payroll.empPayroll.mTOTAL_LEAVES) || 0;
      }
      // Check for Total Paid Leaves
      else if (columnKey === 'totalPaidLeaves') {
        value = Number(payroll.empPayroll.total_paid_leaves) || 0;
      }
      // Check for Total Unpaid Leaves
      else if (columnKey === 'totalUnpaidLeaves') {
        value = Number(payroll.empPayroll.mTOTAL_UNPAID_LEAVES) || 0;
      }
      // Check for Basic
      else if (columnKey === 'basic') {
        value = Number(payroll.empPayroll.mBASIC) || 0;
      }
      // Check for Gross
      else if (columnKey === 'gross') {
        value = Number(payroll.empPayroll.mGROSS) || 0;
      }
      // Check for Profession Tax
      else if (columnKey === 'professionTax') {
        value = Number(payroll.empPayroll.mCALCULATED_PT) || 0;
      }
      // Check for Provident Fund
      else if (columnKey === 'providentFund') {
        value = Number(payroll.empPayroll.mCALCULATED_PF) || 0;
      }
      // Check for Voluntary Provident Fund
      else if (columnKey === 'vpf') {
        value = Number(payroll.empPayroll.mCALCULATED_VPF) || 0;
      }
      // Check for Income Tax
      else if (columnKey === 'incomeTax') {
        value = Number(payroll.income_tax) || 0;
      }
      // Check for Welfare Fund
      else if (columnKey === 'welfareFund') {
        value = Number(payroll.empPayroll.mCALCULATED_WF) || 0;
      }
      // Check for ESIC
      else if (columnKey === 'esic') {
        value = Number(payroll.empPayroll.mCALCULATED_ESI) || 0;
      }
      // Check for Total Deduction
      else if (columnKey === 'totalDeduction') {
        value = Number(payroll.empPayroll.mTOTAL_DEDUCTION) || 0;
      }
      // Check for Net Salary
      else if (columnKey === 'netSalary') {
        value = Number(payroll.empPayroll.mNET_SALARY) || 0;
      }
      // Check for Notice Period Recovery Amount
      else if (columnKey === 'noticePeriodRecoveryAmount') {
        value = Number(payroll.notice_period_recovery_amount) || 0;
      }
      // Check for Remaining Notice Days
      else if (columnKey === 'remainingNoticeDays') {
        value = Number(payroll.remaining_notice_days) || 0;
      }
      // Check for Notice Period Shortfall Days
      else if (columnKey === 'noticePeriodShortfallDays') {
        value = Number(payroll.notice_period_shortfall_days) || 0;
      }
      // Check for Notice Period Pay (payout)
      else if (columnKey === 'noticePeriodPay') {
        value = Number(payroll.notice_period_pay) || 0;
      }
      // Check for Salary Components
      else if (columnKey.startsWith('salComp_')) {
        const index = parseInt(columnKey.replace('salComp_', ''));
        const salItem = payroll.empSalComponents[index];
        const EMP_SAL_FACTOR = payroll.empPayroll.mTOTAL_PRESENT_DAYS / payroll.empPayroll._total_days;
        value = salItem ? Number(salItem.amount) * EMP_SAL_FACTOR || 0 : 0;
      }
      // Check for Earning Components
      else if (columnKey.startsWith('earningComp_')) {
        const index = parseInt(columnKey.replace('earningComp_', ''));
        const earning = earningComps[index];
        if (earning) {
          const matchedComponents = payroll.empEarningComponents?.filter(
            item => item.deduction_earning_type === earning.earningType
          ) || [];
          value = matchedComponents.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
        }
      }
      // Check for Deduction Components
      else if (columnKey.startsWith('deductionComp_')) {
        const index = parseInt(columnKey.replace('deductionComp_', ''));
        const deduction = deductionComps[index];
        if (deduction) {
          const matchedComponents = payroll.empDeductionComponents?.filter(
            item => item.deduction_earning_type === deduction.deductionType
          ) || [];
          value = matchedComponents.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
        }
      }
      // Check for Auto Earning Details
      else if (columnKey.startsWith('autoEarning_')) {
        const edType = columnKey.replace('autoEarning_', '');
        if (payroll.empPayroll?.auto_earning_details && Array.isArray(payroll.empPayroll.auto_earning_details)) {
          const item = payroll.empPayroll.auto_earning_details.find(item => item.ed_type === edType);
          value = Number(item?.amount) || 0;
        }
      }
      // Check for Auto Deduction Details
      else if (columnKey.startsWith('autoDeduction_')) {
        const edType = columnKey.replace('autoDeduction_', '');
        if (payroll.empPayroll?.auto_deduction_details && Array.isArray(payroll.empPayroll.auto_deduction_details)) {
          const item = payroll.empPayroll.auto_deduction_details.find(item => item.ed_type === edType);
          value = Number(item?.amount) || 0;
        }
      }

      return value === 0;
    });
  };

  // --- FILTERED COMPONENTS (Exclude columns with all zeros) ---
  const showTotalDays = !hasAllZeroValues('totalDays', props.payrolls);
  const showTotalWorkedDays = !hasAllZeroValues('totalWorkedDays', props.payrolls);
  const showTotalLeaves = !hasAllZeroValues('totalLeaves', props.payrolls);
  const showTotalPaidLeaves = !hasAllZeroValues('totalPaidLeaves', props.payrolls);
  const showTotalUnpaidLeaves = !hasAllZeroValues('totalUnpaidLeaves', props.payrolls);
  const showBasic = !hasAllZeroValues('basic', props.payrolls);
  const showGross = !hasAllZeroValues('gross', props.payrolls);
  const showProfessionTax = !hasAllZeroValues('professionTax', props.payrolls);
  const showProvidentFund = !hasAllZeroValues('providentFund', props.payrolls);
  const showVpf = !hasAllZeroValues('vpf', props.payrolls);
  const showIncomeTax = !hasAllZeroValues('incomeTax', props.payrolls);
  const showWelfareFund = !hasAllZeroValues('welfareFund', props.payrolls);
  const showEsic = !hasAllZeroValues('esic', props.payrolls);
  const showTotalDeduction = !hasAllZeroValues('totalDeduction', props.payrolls);
  const showNetSalary = !hasAllZeroValues('netSalary', props.payrolls);
  
  // Notice period columns (shown based on type, not zero values)
  const showNoticePeriodRecoveryAmountColumn = showNoticePeriodRecoveryAmount && !hasAllZeroValues('noticePeriodRecoveryAmount', props.payrolls);
  const showRemainingNoticeDaysColumn = showRemainingNoticeDays && !hasAllZeroValues('remainingNoticeDays', props.payrolls);
  const showNoticePeriodShortfallDaysColumn = showNoticePeriodShortfallDays && !hasAllZeroValues('noticePeriodShortfallDays', props.payrolls);
  const showNoticePeriodPayColumn = showNoticePeriodPay && !hasAllZeroValues('noticePeriodPay', props.payrolls);

  // Filter salary components
  const filteredSalComps = props.payrolls[0]?.distinctSalComps?.filter((salItem, index) =>
    !hasAllZeroValues(`salComp_${index}`, props.payrolls)
  ) || [];

  // Filter earning components
  const filteredEarningComps = earningComps.filter((earning, index) =>
    !hasAllZeroValues(`earningComp_${index}`, props.payrolls)
  );

  // Filter deduction components
  const filteredDeductionComps = deductionComps.filter((deduction, index) =>
    !hasAllZeroValues(`deductionComp_${index}`, props.payrolls)
  );

  // --- EXTRACT UNIQUE AUTO EARNING DETAILS ---
  const getUniqueAutoEarningTypes = () => {
    const typeSet = new Set();
    props.payrolls.forEach(payroll => {
      if (payroll.empPayroll?.auto_earning_details && Array.isArray(payroll.empPayroll.auto_earning_details)) {
        payroll.empPayroll.auto_earning_details.forEach(item => {
          if (item.ed_type) {
            typeSet.add(item.ed_type);
          }
        });
      }
    });
    return Array.from(typeSet);
  };

  const uniqueAutoEarningTypes = getUniqueAutoEarningTypes();

  // --- CHECK IF AUTO EARNING COLUMNS SHOULD BE SHOWN ---
  const hasAutoEarningDetails = (edType) => {
    if (!props.payrolls || props.payrolls.length === 0) return false;
    return props.payrolls.some(payroll => {
      if (payroll.empPayroll.mERROR_MESSAGE !== 'No Error') return false;
      if (payroll.empPayroll?.auto_earning_details && Array.isArray(payroll.empPayroll.auto_earning_details)) {
        const item = payroll.empPayroll.auto_earning_details.find(item => item.ed_type === edType);
        return item && (Number(item.amount) || 0) !== 0;
      }
      return false;
    });
  };

  const filteredAutoEarningTypes = uniqueAutoEarningTypes.filter(edType => hasAutoEarningDetails(edType));

  // --- EXTRACT UNIQUE AUTO DEDUCTION DETAILS ---
  const getUniqueAutoDeductionTypes = () => {
    const typeSet = new Set();
    props.payrolls.forEach(payroll => {
      if (payroll.empPayroll?.auto_deduction_details && Array.isArray(payroll.empPayroll.auto_deduction_details)) {
        payroll.empPayroll.auto_deduction_details.forEach(item => {
          if (item.ed_type) {
            typeSet.add(item.ed_type);
          }
        });
      }
    });
    return Array.from(typeSet);
  };

  const uniqueAutoDeductionTypes = getUniqueAutoDeductionTypes();

  // --- CHECK IF AUTO DEDUCTION COLUMNS SHOULD BE SHOWN ---
  const hasAutoDeductionDetails = (edType) => {
    if (!props.payrolls || props.payrolls.length === 0) return false;
    return props.payrolls.some(payroll => {
      if (payroll.empPayroll.mERROR_MESSAGE !== 'No Error') return false;
      if (payroll.empPayroll?.auto_deduction_details && Array.isArray(payroll.empPayroll.auto_deduction_details)) {
        const item = payroll.empPayroll.auto_deduction_details.find(item => item.ed_type === edType);
        return item && (Number(item.amount) || 0) !== 0;
      }
      return false;
    });
  };

  const filteredAutoDeductionTypes = uniqueAutoDeductionTypes.filter(edType => hasAutoDeductionDetails(edType));

  // Calculate totals for all numeric columns
  const calculateTotals = () => {
    if (!props.payrolls || props.payrolls.length === 0) return {};

    const totals = {
      totalDays: 0,
      totalWorkedDays: 0,
      totalLeaves: 0,
      totalPaidLeaves: 0,
      totalUnpaidLeaves: 0,
      basic: 0,
      gross: 0,
      professionTax: 0,
      providentFund: 0,
      vpf: 0,
      incomeTax: 0,
      welfareFund: 0,
      esic: 0,
      totalDeduction: 0,
      netSalary: 0,
      // Notice period fields
      remainingNoticeDays: 0,
      noticePeriodRecoveryAmount: 0,
      noticePeriodShortfallDays: 0,
      noticePeriodPay: 0
    };

    // Initialize totals for salary components
    const salaryComponentTotals = {};
    props.payrolls[0]?.distinctSalComps?.forEach((comp, index) => {
      salaryComponentTotals[`salComp_${index}`] = 0;
    });

    // Initialize totals for earning components
    const earningComponentTotals = {};
    earningComps.forEach((earning, index) => {
      earningComponentTotals[`earning_${index}`] = 0;
    });

    // Initialize totals for deduction components
    const deductionComponentTotals = {};
    deductionComps.forEach((deduction, index) => {
      deductionComponentTotals[`deduction_${index}`] = 0;
    });

    // Initialize totals for auto earning details
    const autoEarningTotals = {};
    uniqueAutoEarningTypes.forEach((edType) => {
      autoEarningTotals[`autoEarning_${edType}`] = 0;
    });

    // Initialize totals for auto deduction details
    const autoDeductionTotals = {};
    uniqueAutoDeductionTypes.forEach((edType) => {
      autoDeductionTotals[`autoDeduction_${edType}`] = 0;
    });

    props.payrolls.forEach((payroll) => {
      if (payroll.empPayroll.mERROR_MESSAGE === 'No Error') {
        const EMP_SAL_FACTOR = payroll.empPayroll.mTOTAL_PRESENT_DAYS / payroll.empPayroll._total_days;
        
        // Basic numeric fields
        totals.totalDays += Number(payroll.empPayroll.daysInMonth) || 0;
        totals.totalWorkedDays += Number(payroll.empPayroll.mTOTAL_PRESENT_DAYS) || 0;
        totals.totalLeaves += Number(payroll.empPayroll.mTOTAL_LEAVES) || 0;
        totals.totalPaidLeaves += Number(payroll.empPayroll.total_paid_leaves) || 0;
        totals.totalUnpaidLeaves += Number(payroll.empPayroll.mTOTAL_UNPAID_LEAVES) || 0;
        totals.basic += Number(payroll.empPayroll.mBASIC) || 0;
        totals.gross += Number(payroll.empPayroll.mGROSS) || 0;
        totals.professionTax += Number(payroll.empPayroll.mCALCULATED_PT) || 0;
        totals.providentFund += Number(payroll.empPayroll.mCALCULATED_PF) || 0;
        totals.vpf += Number(payroll.empPayroll.mCALCULATED_VPF) || 0;
        totals.incomeTax += Number(payroll.income_tax) || 0;
        totals.welfareFund += Number(payroll.empPayroll.mCALCULATED_WF) || 0;
        totals.esic += Number(payroll.empPayroll.mCALCULATED_ESI) || 0;
        totals.totalDeduction += Number(payroll.empPayroll.mTOTAL_DEDUCTION) || 0;
        totals.totalDeduction += Number(payroll.income_tax) || 0; // Add income tax to total deduction
        totals.netSalary += (Number(payroll.empPayroll.mNET_SALARY) || 0) - (Number(payroll.income_tax) || 0); // Subtract income tax from net salary
        
        // Notice period fields
        totals.remainingNoticeDays += Number(payroll.remaining_notice_days) || 0;
        totals.noticePeriodRecoveryAmount += Number(payroll.notice_period_recovery_amount) || 0;
        totals.noticePeriodShortfallDays += Number(payroll.notice_period_shortfall_days) || 0;
        totals.noticePeriodPay += Number(payroll.notice_period_pay) || 0;

        // Salary components
        payroll.empSalComponents.forEach((salItem, index) => {
          const amount = Number(salItem.amount) * EMP_SAL_FACTOR || 0;
          salaryComponentTotals[`salComp_${index}`] += amount;
        });

        // Earning components
        earningComps.forEach((earning, index) => {
          const matchedComponents = payroll.empEarningComponents?.filter(
            item => item.deduction_earning_type === earning.earningType
          ) || [];
          const totalAmount = matchedComponents.reduce(
            (acc, curr) => acc + (Number(curr.amount) || 0),
            0
          );
          earningComponentTotals[`earning_${index}`] += totalAmount;
        });

        // Deduction components
        deductionComps.forEach((deduction, index) => {
          const matchedComponents = payroll.empDeductionComponents?.filter(
            item => item.deduction_earning_type === deduction.deductionType
          ) || [];
          const totalAmount = matchedComponents.reduce(
            (acc, curr) => acc + (Number(curr.amount) || 0),
            0
          );
          deductionComponentTotals[`deduction_${index}`] += totalAmount;
        });

        // Auto earning details
        uniqueAutoEarningTypes.forEach((edType) => {
          if (payroll.empPayroll?.auto_earning_details && Array.isArray(payroll.empPayroll.auto_earning_details)) {
            const item = payroll.empPayroll.auto_earning_details.find(item => item.ed_type === edType);
            const amount = Number(item?.amount) || 0;
            autoEarningTotals[`autoEarning_${edType}`] += amount;
          }
        });

        // Auto deduction details
        uniqueAutoDeductionTypes.forEach((edType) => {
          if (payroll.empPayroll?.auto_deduction_details && Array.isArray(payroll.empPayroll.auto_deduction_details)) {
            const item = payroll.empPayroll.auto_deduction_details.find(item => item.ed_type === edType);
            const amount = Number(item?.amount) || 0;
            autoDeductionTotals[`autoDeduction_${edType}`] += amount;
          }
        });
      }
    });

    return {
      ...totals,
      ...salaryComponentTotals,
      ...earningComponentTotals,
      ...autoEarningTotals,
      ...autoDeductionTotals,
      ...deductionComponentTotals
    };
  };

  const totals = calculateTotals();

  // Improved scroll sync
  useEffect(() => {
    const headerContainer = document.getElementById('payroll-table-header-container');
    const bodyContainer = document.getElementById('payroll-table-body-container');

    const syncScroll = () => {
      if (headerContainer && bodyContainer) {
        headerContainer.scrollLeft = bodyContainer.scrollLeft;
      }
    };

    bodyContainer?.addEventListener('scroll', syncScroll);

    return () => {
      bodyContainer?.removeEventListener('scroll', syncScroll);
    };
  }, [props.isLoading]);

  // Column widths for CreatedPayrollTable
  const columnWidths = {
    srNo: '80px',
    employeeName: '200px',
    totalDays: '150px',
    totalWorkedDays: '150px',
    totalLeaves: '120px',
    totalPaidLeaves: '150px',
    totalUnpaidLeaves: '150px',
    bankAccount: '180px',
    panNumber: '150px',
    pfNumber: '150px',
    esiNumber: '150px',
    basic: '120px',
    gross: '120px',
    professionTax: '120px',
    providentFund: '120px',
    vpf: '120px',
    incomeTax: '120px',
    welfareFund: '150px',
    esic: '120px',
    totalDeduction: '150px',
    netSalary: '150px',
    salaryComponent: '150px',
    earningComponent: '150px',
    deductionComponent: '150px',
    autoEarning: '150px',
    autoDeduction: '150px',
    // New notice period columns
    remainingNoticeDays: '150px',
    noticePeriodRecoveryAmount: '180px',
    noticePeriodShortfallDays: '180px',
    noticePeriodPay: '180px'
  };

  // Calculate total width based on visible columns
  const calculateTotalWidth = () => {
    let totalWidth = parseInt(columnWidths.srNo) + parseInt(columnWidths.employeeName);
    
    if (showTotalDays) totalWidth += parseInt(columnWidths.totalDays);
    if (showTotalWorkedDays) totalWidth += parseInt(columnWidths.totalWorkedDays);
    if (showTotalLeaves) totalWidth += parseInt(columnWidths.totalLeaves);
    if (showTotalPaidLeaves) totalWidth += parseInt(columnWidths.totalPaidLeaves);
    if (showTotalUnpaidLeaves) totalWidth += parseInt(columnWidths.totalUnpaidLeaves);
    
    totalWidth += parseInt(columnWidths.bankAccount) + parseInt(columnWidths.panNumber) + 
                  parseInt(columnWidths.pfNumber) + parseInt(columnWidths.esiNumber);
    
    if (showBasic) totalWidth += parseInt(columnWidths.basic);
    
    // Add salary components
    totalWidth += filteredSalComps.length * parseInt(columnWidths.salaryComponent);
    
    // Add earning components
    totalWidth += filteredEarningComps.length * parseInt(columnWidths.earningComponent);
    
    // Add auto earning details
    totalWidth += filteredAutoEarningTypes.length * parseInt(columnWidths.autoEarning);
    
    // Add auto deduction details
    totalWidth += filteredAutoDeductionTypes.length * parseInt(columnWidths.autoDeduction);
    
    // Notice period columns before Gross
    if (showRemainingNoticeDaysColumn) totalWidth += parseInt(columnWidths.remainingNoticeDays);
    if (showNoticePeriodRecoveryAmountColumn) totalWidth += parseInt(columnWidths.noticePeriodRecoveryAmount);
    if (showNoticePeriodShortfallDaysColumn) totalWidth += parseInt(columnWidths.noticePeriodShortfallDays);
    if (showNoticePeriodPayColumn) totalWidth += parseInt(columnWidths.noticePeriodPay);
    
    if (showGross) totalWidth += parseInt(columnWidths.gross);
    if (showProfessionTax) totalWidth += parseInt(columnWidths.professionTax);
    if (showProvidentFund) totalWidth += parseInt(columnWidths.providentFund);
    if (showVpf) totalWidth += parseInt(columnWidths.vpf);
    if (showIncomeTax) totalWidth += parseInt(columnWidths.incomeTax);
    if (showWelfareFund) totalWidth += parseInt(columnWidths.welfareFund);
    if (showEsic) totalWidth += parseInt(columnWidths.esic);
    
    // Add deduction components
    totalWidth += filteredDeductionComps.length * parseInt(columnWidths.deductionComponent);
    
    if (showTotalDeduction) totalWidth += parseInt(columnWidths.totalDeduction);
    if (showNetSalary) totalWidth += parseInt(columnWidths.netSalary);
    
    return totalWidth;
  };

  const totalWidth = calculateTotalWidth();

  // Helper function to create consistent column styles
  const getColumnStyle = (width, textAlign = 'center') => ({
    width: width,
    minWidth: width,
    maxWidth: width,
    padding: '8px',
    boxSizing: 'border-box',
    textAlign: textAlign,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  });

  return (
    <div>
      {props.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : props.payrolls && props.payrolls.length > 0 ? (
        <div style={{ position: 'relative', height: '500px', display: 'flex', flexDirection: 'column' }}>
          
          {/* Fixed Header */}
          <Box 
            id="payroll-table-header-container"
            sx={{ 
              overflowX: 'hidden',
              overflowY: 'hidden',
              position: 'sticky', 
              top: 0, 
              zIndex: 10, 
              background: '#fff',
              flexShrink: 0,
              borderBottom: '2px solid #dee2e6'
            }}
          >
            <table style={{ 
              width: `${totalWidth}px`, 
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              margin: 0,
              borderSpacing: 0
            }} className="table table-dark">
              <thead>
                <tr>
                  <th style={getColumnStyle(columnWidths.srNo)}>Sr. No</th>
                  <th style={getColumnStyle(columnWidths.employeeName, 'left')}>Employee Name</th>
                  {showTotalDays && <th style={getColumnStyle(columnWidths.totalDays)}>Total no of days</th>}
                  {showTotalWorkedDays && <th style={getColumnStyle(columnWidths.totalWorkedDays)}>Total Worked Days</th>}
                  {showTotalLeaves && <th style={getColumnStyle(columnWidths.totalLeaves)}>Total Leaves</th>}
                  {showTotalPaidLeaves && <th style={getColumnStyle(columnWidths.totalPaidLeaves)}>Total Paid Leaves</th>}
                  {showTotalUnpaidLeaves && <th style={getColumnStyle(columnWidths.totalUnpaidLeaves)}>Total Unpaid Leaves</th>}
                  <th style={getColumnStyle(columnWidths.bankAccount)}>Bank account no</th>
                  <th style={getColumnStyle(columnWidths.panNumber)}>PAN Number</th>
                  <th style={getColumnStyle(columnWidths.pfNumber)}>PF number</th>
                  <th style={getColumnStyle(columnWidths.esiNumber)}>ESI number</th>
                  {showBasic && <th style={getColumnStyle(columnWidths.basic)}>Basic</th>}
                  
                  {/* Filtered Salary Components */}
                  {filteredSalComps.map((salItem, index) =>
                    <th key={index} style={getColumnStyle(columnWidths.salaryComponent)}>{salItem.component}</th>
                  )}
                  
                  {/* Filtered Earning Components */}
                  {filteredEarningComps.map((item,index) =>
                    <th key={index} style={getColumnStyle(columnWidths.earningComponent)}>{item.earningType}</th>
                  )}
                  
                  {/* Auto Earning Details */}
                  {filteredAutoEarningTypes.map((edType, index) =>
                    <th key={index} style={getColumnStyle(columnWidths.autoEarning)}>{edType}</th>
                  )}
                  
                  {/* Auto Deduction Details */}
                  {filteredAutoDeductionTypes.map((edType, index) =>
                    <th key={index} style={getColumnStyle(columnWidths.autoDeduction)}>{edType}</th>
                  )}
                  
                  {/* Notice Period Columns - before Gross */}
                  {showRemainingNoticeDaysColumn && 
                    <th style={getColumnStyle(columnWidths.remainingNoticeDays)}>Remaining Notice Days</th>
                  }
                  {showNoticePeriodRecoveryAmountColumn && 
                    <th style={getColumnStyle(columnWidths.noticePeriodRecoveryAmount)}>Notice Period Recovery Amount</th>
                  }
                  {showNoticePeriodShortfallDaysColumn && 
                    <th style={getColumnStyle(columnWidths.noticePeriodShortfallDays)}>Notice Period Shortfall Days</th>
                  }
                  {showNoticePeriodPayColumn && 
                    <th style={getColumnStyle(columnWidths.noticePeriodPay)}>Notice Period Pay</th>
                  }
                  
                  {showGross && <th style={getColumnStyle(columnWidths.gross)}>Gross</th>}
                  {showProfessionTax && <th style={getColumnStyle(columnWidths.professionTax)}>PT</th>}
                  {showProvidentFund && <th style={getColumnStyle(columnWidths.providentFund)}>PF</th>}
                  {showVpf && <th style={getColumnStyle(columnWidths.vpf)}>VPF</th>}
                  {showIncomeTax && <th style={getColumnStyle(columnWidths.incomeTax)}>Income Tax</th>}
                  {showWelfareFund && <th style={getColumnStyle(columnWidths.welfareFund)}>Welfare Fund</th>}
                  {showEsic && <th style={getColumnStyle(columnWidths.esic)}>ESI</th>}
                  
                  {/* Filtered Deduction Components */}
                  {filteredDeductionComps.map((item,index) =>
                    <th key={index} style={getColumnStyle(columnWidths.deductionComponent)}>{item.deductionType}</th>
                  )}
                  
                  {showTotalDeduction && <th style={getColumnStyle(columnWidths.totalDeduction)}>Total Deduction</th>}
                  {showNetSalary && <th style={getColumnStyle(columnWidths.netSalary)}>Net Pay Rs</th>}
                </tr>
              </thead>
            </table>
          </Box>

          {/* Scrollable Body */}
          <Box
            id="payroll-table-body-container"
            sx={{ 
              overflowY: 'auto', 
              overflowX: 'auto', 
              flex: 1,
              minHeight: 0
            }}
          >
            <table style={{ 
              width: `${totalWidth}px`, 
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              margin: 0,
              borderSpacing: 0
            }} className="table">
              <tbody>
                {props.payrolls.map((payroll, i) => {
                  const EMP_SAL_FACTOR = payroll.empPayroll.mTOTAL_PRESENT_DAYS / payroll.empPayroll._total_days;
                  return (
                    <React.Fragment key={i}>
                      {payroll.empPayroll.mERROR_MESSAGE === 'No Error' ? (
                        <tr>
                          <td style={getColumnStyle(columnWidths.srNo)}>{i + 1}</td>
                          <td style={getColumnStyle(columnWidths.employeeName, 'left')}>{payroll.empPersonalDetails.first_name + " " + payroll.empPersonalDetails.last_name}</td>
                          {showTotalDays && <td style={getColumnStyle(columnWidths.totalDays)}>{payroll.empPayroll.daysInMonth ? payroll.empPayroll.daysInMonth : '0'}</td>}
                          {showTotalWorkedDays && <td style={getColumnStyle(columnWidths.totalWorkedDays)}>{payroll.empPayroll.mTOTAL_PRESENT_DAYS ? payroll.empPayroll.mTOTAL_PRESENT_DAYS : '0'}</td>}
                          {showTotalLeaves && <td style={getColumnStyle(columnWidths.totalLeaves)}>{payroll.empPayroll.mTOTAL_LEAVES ? payroll.empPayroll.mTOTAL_LEAVES : '0'}</td>}
                          {showTotalPaidLeaves && <td style={getColumnStyle(columnWidths.totalPaidLeaves)}>{payroll.empPayroll.total_paid_leaves ? payroll.empPayroll.total_paid_leaves : '0'}</td>}
                          {showTotalUnpaidLeaves && <td style={getColumnStyle(columnWidths.totalUnpaidLeaves)}>{payroll.empPayroll.mTOTAL_UNPAID_LEAVES ? payroll.empPayroll.mTOTAL_UNPAID_LEAVES : '0'}</td>}
                          <td style={getColumnStyle(columnWidths.bankAccount)}>{payroll.empPersonalDetails.ICICI_account_no ? payroll.empPersonalDetails.ICICI_account_no : '0'}</td>
                          <td style={getColumnStyle(columnWidths.panNumber)}>{payroll.empPersonalDetails.pan_card_no ? payroll.empPersonalDetails.pan_card_no : '0'}</td>
                          <td style={getColumnStyle(columnWidths.pfNumber)}>{payroll.empPersonalDetails.pf_number ? payroll.empPersonalDetails.pf_number : '0'}</td>
                          <td style={getColumnStyle(columnWidths.esiNumber)}>{payroll.empPersonalDetails.ESINumber ? payroll.empPersonalDetails.ESINumber : '0'}</td>
                          {showBasic && <td style={getColumnStyle(columnWidths.basic)}>{payroll.empPayroll.mBASIC ? Math.round(payroll.empPayroll.mBASIC).toLocaleString('en-US') : '0'}</td>}
                          
                          {/* Filtered Salary Components */}
                          {filteredSalComps.map((salItem, salIndex) => {
                            const component = payroll.empSalComponents.find(comp => comp.component === salItem.component);
                            return (
                              <td key={salIndex} style={getColumnStyle(columnWidths.salaryComponent)}>
                                {component?.amount ? Math.round(component.amount * EMP_SAL_FACTOR).toLocaleString('en-US') : '0'}
                              </td>
                            );
                          })}
                          
                          {/* Filtered Earning Components */}
                          {filteredEarningComps.map((earning, index) => {
                            const matchedComponents = payroll.empEarningComponents?.filter(
                              item => item.deduction_earning_type === earning.earningType
                            ) || [];

                            const totalAmount = matchedComponents.reduce(
                              (acc, curr) => acc + (curr.amount || 0),
                              0
                            );

                            return <td key={index} style={getColumnStyle(columnWidths.earningComponent)}>{totalAmount.toLocaleString('en-US')}</td>;
                          })}
                          
                          {/* Auto Earning Details */}
                          {filteredAutoEarningTypes.map((edType, index) => {
                            let amount = 0;
                            if (payroll.empPayroll?.auto_earning_details && Array.isArray(payroll.empPayroll.auto_earning_details)) {
                              const item = payroll.empPayroll.auto_earning_details.find(item => item.ed_type === edType);
                              amount = Number(item?.amount) || 0;
                            }
                            return <td key={index} style={getColumnStyle(columnWidths.autoEarning)}>{amount.toLocaleString('en-US')}</td>;
                          })}
                          
                          {/* Auto Deduction Details */}
                          {filteredAutoDeductionTypes.map((edType, index) => {
                            let amount = 0;
                            if (payroll.empPayroll?.auto_deduction_details && Array.isArray(payroll.empPayroll.auto_deduction_details)) {
                              const item = payroll.empPayroll.auto_deduction_details.find(item => item.ed_type === edType);
                              amount = Number(item?.amount) || 0;
                            }
                            return <td key={index} style={getColumnStyle(columnWidths.autoDeduction)}>{amount.toLocaleString('en-US')}</td>;
                          })}
                          
                          {/* Notice Period Columns - before Gross */}
                          {showRemainingNoticeDaysColumn && 
                            <td style={getColumnStyle(columnWidths.remainingNoticeDays)}>
                              {payroll.remaining_notice_days ? Math.round(payroll.remaining_notice_days) : '0'}
                            </td>
                          }
                          {showNoticePeriodRecoveryAmountColumn && 
                            <td style={getColumnStyle(columnWidths.noticePeriodRecoveryAmount)}>
                              {payroll.notice_period_recovery_amount ? Math.round(payroll.notice_period_recovery_amount).toLocaleString('en-US') : '0'}
                            </td>
                          }
                          {showNoticePeriodShortfallDaysColumn && 
                            <td style={getColumnStyle(columnWidths.noticePeriodShortfallDays)}>
                              {payroll.notice_period_shortfall_days ? Math.round(payroll.notice_period_shortfall_days) : '0'}
                            </td>
                          }
                          {showNoticePeriodPayColumn && 
                            <td style={getColumnStyle(columnWidths.noticePeriodPay)}>
                              {payroll.notice_period_pay ? Math.round(payroll.notice_period_pay).toLocaleString('en-US') : '0'}
                            </td>
                          }
                          
                          {showGross && <td style={getColumnStyle(columnWidths.gross)}>{payroll.empPayroll.mGROSS ? Math.round(payroll.empPayroll.mGROSS).toLocaleString('en-US') : '0'}</td>}
                          {showProfessionTax && <td style={getColumnStyle(columnWidths.professionTax)}>{payroll.empPayroll.mCALCULATED_PT ? Math.round(payroll.empPayroll.mCALCULATED_PT).toLocaleString('en-US') : '0'}</td>}
                          {showProvidentFund && (() => {
                            const pfAmount = payroll.empPayroll.mCALCULATED_PF ? Math.round(payroll.empPayroll.mCALCULATED_PF).toLocaleString('en-US') : '0';
                            const pfSplitTooltip = getPfSplitTooltip(payroll.empPayroll.mPF_RULE_BREAKDOWN);
                            return (
                              <td style={getColumnStyle(columnWidths.providentFund)}>
                                {pfSplitTooltip ? (
                                  <Tooltip title={pfSplitTooltip} arrow>
                                    <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>{pfAmount}*</span>
                                  </Tooltip>
                                ) : pfAmount}
                              </td>
                            );
                          })()}
                          {showVpf && <td style={getColumnStyle(columnWidths.vpf)}>{payroll.empPayroll.mCALCULATED_VPF ? Math.round(payroll.empPayroll.mCALCULATED_VPF).toLocaleString('en-US') : '0'}</td>}
                          {showIncomeTax && <td style={getColumnStyle(columnWidths.incomeTax)}>{payroll.income_tax ? Math.round(payroll.income_tax).toLocaleString('en-US') : '0'}</td>}
                          {showWelfareFund && <td style={getColumnStyle(columnWidths.welfareFund)}>{payroll.empPayroll.mCALCULATED_WF ? Math.round(payroll.empPayroll.mCALCULATED_WF).toLocaleString('en-US') : '0'}</td>}
                          {showEsic && <td style={getColumnStyle(columnWidths.esic)}>{payroll.empPayroll.mCALCULATED_ESI ? Math.ceil(payroll.empPayroll.mCALCULATED_ESI).toLocaleString('en-US') : '0'}</td>}
                          
                          {/* Filtered Deduction Components */}
                          {filteredDeductionComps.map((deduction, index) => {
                            const matchedComponents = payroll.empDeductionComponents?.filter(
                              item => item.deduction_earning_type === deduction.deductionType
                            ) || [];

                            const totalAmount = matchedComponents.reduce(
                              (acc, curr) => acc + (curr.amount || 0),
                              0
                            );

                            return <td key={index} style={getColumnStyle(columnWidths.deductionComponent)}>{totalAmount.toLocaleString('en-US')}</td>;
                          })}
                          
                          {showTotalDeduction && <td style={getColumnStyle(columnWidths.totalDeduction)}>{((payroll.empPayroll.mTOTAL_DEDUCTION || 0) + (payroll.income_tax || 0)) ? Math.round((payroll.empPayroll.mTOTAL_DEDUCTION || 0) + (payroll.income_tax || 0)).toLocaleString('en-US') : '0'}</td>}
                          {showNetSalary && <td style={getColumnStyle(columnWidths.netSalary)}><b>{((payroll.empPayroll.mNET_SALARY || 0) - (payroll.income_tax || 0)) ? Math.round((payroll.empPayroll.mNET_SALARY || 0) - (payroll.income_tax || 0)).toLocaleString('en-US') : '0'}</b></td>}
                        </tr>
                      ) : (
                        <SnackbarMUI message={payroll.empPayroll.mERROR_MESSAGE + ' for ' + payroll.empPersonalDetails.first_name} />
                      )}
                    </React.Fragment>
                  )
                })}
              
                {/* Totals Row */}
                {props.payrolls.length > 0 && (
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', position: 'sticky', bottom: 0 }}>
                    <td style={getColumnStyle(columnWidths.srNo)}></td>
                    <td style={getColumnStyle(columnWidths.employeeName)}>Total</td>
                    {showTotalDays && <td style={getColumnStyle(columnWidths.totalDays)}>{Math.round(totals.totalDays || 0)}</td>}
                    {showTotalWorkedDays && <td style={getColumnStyle(columnWidths.totalWorkedDays)}>{Math.round(totals.totalWorkedDays || 0)}</td>}
                    {showTotalLeaves && <td style={getColumnStyle(columnWidths.totalLeaves)}>{Math.round(totals.totalLeaves || 0)}</td>}
                    {showTotalPaidLeaves && <td style={getColumnStyle(columnWidths.totalPaidLeaves)}>{Math.round(totals.totalPaidLeaves || 0)}</td>}
                    {showTotalUnpaidLeaves && <td style={getColumnStyle(columnWidths.totalUnpaidLeaves)}>{Math.round(totals.totalUnpaidLeaves || 0)}</td>}
                    <td style={getColumnStyle(columnWidths.bankAccount)}></td>
                    <td style={getColumnStyle(columnWidths.panNumber)}></td>
                    <td style={getColumnStyle(columnWidths.pfNumber)}></td>
                    <td style={getColumnStyle(columnWidths.esiNumber)}></td>
                    {showBasic && <td style={getColumnStyle(columnWidths.basic)}>{Math.round(totals.basic || 0).toLocaleString('en-US')}</td>}
                    
                    {/* Filtered Salary Components Totals */}
                    {filteredSalComps.map((salItem, index) => {
                      const originalIndex = props.payrolls[0]?.distinctSalComps?.findIndex(comp => comp.component === salItem.component) || 0;
                      return (
                        <td key={index} style={getColumnStyle(columnWidths.salaryComponent)}>{Math.round(totals[`salComp_${originalIndex}`] || 0).toLocaleString('en-US')}</td>
                      );
                    })}
                    
                    {/* Filtered Earning Components Totals */}
                    {filteredEarningComps.map((earning, index) => {
                      const originalIndex = earningComps.findIndex(comp => comp.earningType === earning.earningType);
                      return (
                        <td key={index} style={getColumnStyle(columnWidths.earningComponent)}>{Math.round(totals[`earning_${originalIndex}`] || 0).toLocaleString('en-US')}</td>
                      );
                    })}
                    
                    {/* Auto Earning Details Totals */}
                    {filteredAutoEarningTypes.map((edType, index) => {
                      return (
                        <td key={index} style={getColumnStyle(columnWidths.autoEarning)}>{Math.round(totals[`autoEarning_${edType}`] || 0).toLocaleString('en-US')}</td>
                      );
                    })}
                    
                    {/* Auto Deduction Details Totals */}
                    {filteredAutoDeductionTypes.map((edType, index) => {
                      return (
                        <td key={index} style={getColumnStyle(columnWidths.autoDeduction)}>{Math.round(totals[`autoDeduction_${edType}`] || 0).toLocaleString('en-US')}</td>
                      );
                    })}
                    
                    {/* Notice Period Totals - before Gross */}
                    {showRemainingNoticeDaysColumn && 
                      <td style={getColumnStyle(columnWidths.remainingNoticeDays)}>{Math.round(totals.remainingNoticeDays || 0)}</td>
                    }
                    {showNoticePeriodRecoveryAmountColumn && 
                      <td style={getColumnStyle(columnWidths.noticePeriodRecoveryAmount)}>{Math.round(totals.noticePeriodRecoveryAmount || 0).toLocaleString('en-US')}</td>
                    }
                    {showNoticePeriodShortfallDaysColumn && 
                      <td style={getColumnStyle(columnWidths.noticePeriodShortfallDays)}>{Math.round(totals.noticePeriodShortfallDays || 0)}</td>
                    }
                    {showNoticePeriodPayColumn && 
                      <td style={getColumnStyle(columnWidths.noticePeriodPay)}>{Math.round(totals.noticePeriodPay || 0).toLocaleString('en-US')}</td>
                    }
                    
                    {showGross && <td style={getColumnStyle(columnWidths.gross)}>{Math.round(totals.gross || 0).toLocaleString('en-US')}</td>}
                    {showProfessionTax && <td style={getColumnStyle(columnWidths.professionTax)}>{Math.round(totals.professionTax || 0).toLocaleString('en-US')}</td>}
                    {showProvidentFund && <td style={getColumnStyle(columnWidths.providentFund)}>{Math.round(totals.providentFund || 0).toLocaleString('en-US')}</td>}
                    {showVpf && <td style={getColumnStyle(columnWidths.vpf)}>{Math.round(totals.vpf || 0).toLocaleString('en-US')}</td>}
                    {showIncomeTax && <td style={getColumnStyle(columnWidths.incomeTax)}>{Math.round(totals.incomeTax || 0).toLocaleString('en-US')}</td>}
                    {showWelfareFund && <td style={getColumnStyle(columnWidths.welfareFund)}>{Math.round(totals.welfareFund || 0).toLocaleString('en-US')}</td>}
                    {showEsic && <td style={getColumnStyle(columnWidths.esic)}>{Math.ceil(totals.esic || 0).toLocaleString('en-US')}</td>}
                    
                    {/* Filtered Deduction Components Totals */}
                    {filteredDeductionComps.map((deduction, index) => {
                      const originalIndex = deductionComps.findIndex(comp => comp.deductionType === deduction.deductionType);
                      return (
                        <td key={index} style={getColumnStyle(columnWidths.deductionComponent)}>{Math.round(totals[`deduction_${originalIndex}`] || 0).toLocaleString('en-US')}</td>
                      );
                    })}
                    
                    {showTotalDeduction && <td style={getColumnStyle(columnWidths.totalDeduction)}>{Math.round(totals.totalDeduction || 0).toLocaleString('en-US')}</td>}
                    {showNetSalary && <td style={getColumnStyle(columnWidths.netSalary)}><b>{Math.round(totals.netSalary || 0).toLocaleString('en-US')}</b></td>}
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </div>
      ) : (
        <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
          No payroll data available.
        </Typography>
      )}
    </div>
  );
}

// Add a prop-type check for the new isLoading prop
CreatedPayrollTable.propTypes = {
  payrolls: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
};